/**
 * Quick Capture Component
 *
 * Fast note input optimized for speed:
 * - Minimal friction
 * - Keyboard-first UX
 * - Cmd/Ctrl+Enter to save
 */

import { useState, useCallback, type KeyboardEvent } from 'react';
import './QuickCapture.css';

interface QuickCaptureProps {
  onCapture: (content: string) => void;
}

export function QuickCapture({ onCapture }: QuickCaptureProps): JSX.Element {
  const [content, setContent] = useState('');
  const [isFocused, setIsFocused] = useState(false);

  const handleSubmit = useCallback(() => {
    if (content.trim()) {
      onCapture(content.trim());
      setContent('');
    }
  }, [content, onCapture]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <div className={`quick-capture ${isFocused ? 'quick-capture--focused' : ''}`}>
      <div className="quick-capture__icon">
        <span>✏️</span>
      </div>
      <textarea
        className="quick-capture__input"
        placeholder="Quick note... (Cmd+Enter to save)"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        rows={1}
      />
      <button
        type="button"
        className="quick-capture__submit"
        onClick={handleSubmit}
        disabled={!content.trim()}
      >
        Save
      </button>
    </div>
  );
}
